import { useState, useCallback, useEffect, Dispatch, SetStateAction } from 'react';
import { PrimaryButton, DefaultButton, IComboBoxOption, Spinner, SpinnerSize } from '@fluentui/react';
import TimeTaxSupportSystemClient from '../../../../system';
import MessageCourier from '../../../../lib/MessageCourier';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import { User } from '../../../../system/User';
import { DataEditor, GridCellKind, Item, GridCell, GridColumn, GridSelection, CompactSelection } from '@glideapps/glide-data-grid';
import { DropdownCell, DropdownRenderer } from '../../../../lib/GlideDataGrid/ComboxGlideGrid';
import '@glideapps/glide-data-grid/dist/index.css';
import { IComboBoxOptionGrid } from '../../../CargaPorTabla/InterfaceTabla';
import DialogConfirmRecoveredData from '../components/DialogConfirmRecoveredData';
import { BillableDistributionTemplateItem } from '../../../../system/report_hours_settlement/ReportHoursSettlementSystem';

interface IGenerateSettlement {
  setButtonDisability?: any;
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  generateSettlement: () => void;
  billableDistributionTemplate: BillableDistributionTemplateItem[];
  setBillableDistributionTemplate: Dispatch<SetStateAction<BillableDistributionTemplateItem[]>>;
  settlementType: IComboBoxOption;
  from: DateTime;
  period: string;
  user: User;
}

interface IRow {
  id: string;
  empresa: IComboBoxOptionGrid;
  area: IComboBoxOptionGrid;
  empresaDestino: IComboBoxOptionGrid;
}

const EMPTY_OPTION: IComboBoxOptionGrid = { value: '', label: '' };

const DEFAULT_ROWS: IRow[] = Array.from({ length: 10 }, (_, i) => ({
  id: String(i),
  isNew: true,
  empresa: { ...EMPTY_OPTION },
  area: { ...EMPTY_OPTION },
  empresaDestino: { ...EMPTY_OPTION },
}));

const CostDerivationReportHoursSettlement = (props: IGenerateSettlement) => {
  const [addRows, setAddRows] = useState<IRow[]>(DEFAULT_ROWS);
  const [listCompanies, setListCompanies] = useState<IComboBoxOptionGrid[]>([]);
  const [listNodes, setListNodes] = useState<IComboBoxOptionGrid[]>([]);
  const [showRecoveredDataDialog, setShowRecoveredDataDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  const [gridSelection, setGridSelection] = useState<GridSelection>({
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty(),
    current: undefined,
  });

  const [columns] = useState<GridColumn[]>([
    {
      id: 'empresa',
      title: t('EMPRESA ORIGEN'),
      width: 220,
    },
    {
      id: 'area',
      title: t('AREA'),
      width: 220,
    },
    {
      id: 'empresaDestino',
      title: t('EMPRESA DESTINO'),
      width: 220,
    },
  ]);

  const EMPTY_CELL: GridCell = {
    kind: GridCellKind.Text,
    data: '',
    displayData: '',
    allowOverlay: false,
  };

  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const item = addRows[row];

      switch (col) {
        case 0:
          return {
            kind: GridCellKind.Custom,
            allowOverlay: true,
            copyData: item?.empresa.label || '',
            cursor: 'pointer',
            data: {
              kind: 'dropdown-cell',
              value: {
                value: item.empresa.value,
                label: item.empresa.label,
              },
              displayData: item?.empresa.label ?? '',
              options: listCompanies,
              placeholder: t('Empresa Origen'),
              id: item.id,
              width: '150px',
              alignContent: 'left',
              isEditableInputText: true,
              onChange: (option: IComboBoxOptionGrid) => {
                setAddRows((prev) => {
                  const next = [...prev];
                  next[row] = { ...next[row], empresa: option ?? { value: '', label: '' } };
                  return next;
                });
              },
            },
          } as DropdownCell;

        case 1:
          return {
            kind: GridCellKind.Custom,
            allowOverlay: true,
            copyData: item?.area.label || '',
            cursor: 'pointer',
            data: {
              kind: 'dropdown-cell',
              value: {
                value: item.area.value,
                label: item.area.label,
              },
              displayData: item?.area.label ?? '',
              options: listNodes,
              placeholder: t('Area'),
              id: item.id,
              width: '150px',
              alignContent: 'left',
              isEditableInputText: true,
              onChange: (option: IComboBoxOptionGrid) => {
                setAddRows((prev) => {
                  const next = [...prev];
                  next[row] = { ...next[row], area: option ?? EMPTY_OPTION };
                  return next;
                });
              },
            },
          } as DropdownCell;

        case 2:
          return {
            kind: GridCellKind.Custom,
            allowOverlay: true,
            copyData: item?.empresaDestino.label || '',
            cursor: 'pointer',
            data: {
              kind: 'dropdown-cell',
              value: {
                value: item.empresaDestino.value,
                label: item.empresaDestino.label,
              },
              displayData: item?.empresaDestino.label ?? '',
              options: listCompanies,
              placeholder: t('Empresa Destino'),
              id: item.id,
              width: '150px',
              alignContent: 'left',
              isEditableInputText: true,
              onChange: (option: IComboBoxOptionGrid) => {
                setAddRows((prev) => {
                  const next = [...prev];
                  next[row] = { ...next[row], empresaDestino: option ?? { value: '', label: '' } };
                  return next;
                });
              },
            },
          } as DropdownCell;

        default:
          return EMPTY_CELL;
      }
    },
    [addRows, listCompanies, listNodes],
  );

  const handleCellEdited = useCallback((_cell: Item, _newVal: GridCell) => {
    // todas las columnas usan DropdownCell con onChange propio
  }, []);

  const handleEliminar = useCallback(() => {
    const selectedIndexes = gridSelection.rows.toArray();
    if (selectedIndexes.length === 0) return;

    setAddRows((prev) => prev.filter((_, i) => !selectedIndexes.includes(i)));
    setGridSelection({
      columns: CompactSelection.empty(),
      rows: CompactSelection.empty(),
      current: undefined,
    });
  }, [gridSelection.rows]);

  const handleLoadCompaniesInBillableDistribution = async () => {
    try {
      const data = await props.system.getReportHoursSettlementSystem().getCompaniesInBillableDistribution();
      props.setBillableDistributionTemplate(data);
      setAddRows(data.length === 0 ? DEFAULT_ROWS : data);
    } catch (error: any) {
      props.courier.messageError(error?.message || error);
    }
  };

  const handleListCompanies = async () => {
    const period = new Date();
    const periodFormat = period.toISOString().split('T')[0];
    const data = await props.system.getCompanySystem().getListComapniesActives(periodFormat);
    const options: IComboBoxOptionGrid[] = data.map((country) => ({
      value: country.getId(),
      label: country.getName(),
    }));
    setListCompanies(options);
  };

  const handleListNodes = async () => {
    const levelsResponse = await props.system.getOrganizationChartSystem().getLevels();
    const lastLevel = levelsResponse[levelsResponse.length - 1];

    const organigramaData = await props.system.getOrganizationChartSystem().getLastLevelNodesss(lastLevel.getLevel().toString());

    const options: IComboBoxOptionGrid[] = organigramaData.levels.map((node: any) => ({
      value: node.key,
      label: node.text,
    }));

    setListNodes(options);
  };

  const handleConfirmRecoveredDataDialog = async () => {
    props.courier.messageWarning('');
    const rowsWithoutEmpty = handleDataClean();
    updateCompaniesInBillableDistribution(rowsWithoutEmpty);
    setShowRecoveredDataDialog(false);
  };

  const updateCompaniesInBillableDistribution = async (rows: IRow[]) => {
    props.courier.messageError('');
    props.setBillableDistributionTemplate([]);
    setAddRows([]);
    try {
      setLoading(true);
      console.log('val-month', props.period);
      await props.system.getReportHoursSettlementSystem().updateCompaniesInBillableDistribution({
        period: props.period,
        companies: rows,
      });
      await handleLoadCompaniesInBillableDistribution();
      props.courier.messageSuccess(t('Empresas actualizadas correctamente'));
    } catch (error: any) {
      props.courier.messageError(error?.message || error);
    } finally {
      setLoading(false);
      // props.changeBlockUI(false);
    }
  };

  const handleDataClean = () => {
    const rowsWithoutEmpty = addRows.filter((row) => {
      return row.empresa.value !== '' || row.area.value !== '' || row.empresaDestino.value !== '';
    });
    setAddRows(rowsWithoutEmpty);
    return rowsWithoutEmpty;
  };

  const handleOpenModel = () => {
    setShowRecoveredDataDialog(true);
  };

  const handleCancelRecoveredDataDialog = () => {
    setShowRecoveredDataDialog(false);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);

      try {
        await Promise.all([handleListCompanies(), handleListNodes()]);

        await handleLoadCompaniesInBillableDistribution();
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          height: '200px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spinner size={SpinnerSize.large} label="Cargando configuración..." />
      </div>
    );
  }

  return (
    <div>
      <div className="ms-Grid mt-2" dir="ltr">
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12">
            <DataEditor
              customRenderers={[DropdownRenderer]}
              getCellContent={getCellContent}
              onCellEdited={handleCellEdited}
              columns={columns}
              rows={addRows.length}
              smoothScrollX
              smoothScrollY
              getCellsForSelection={true}
              rowMarkers="checkbox"
              rowSelect="multi"
              gridSelection={gridSelection}
              onGridSelectionChange={setGridSelection}
              keybindings={{
                selectAll: true,
                copy: true,
                paste: true,
                delete: true,
              }}
            />
            <div className="ms-Grid-col ms-sm12 mt-4" style={{ display: 'flex', gap: 8 }}>
              <PrimaryButton
                text={t('Agregar Fila')}
                onClick={() =>
                  setAddRows((prev) => [
                    ...prev,
                    {
                      id: String(prev.length),
                      isNew: true,
                      empresa: { ...EMPTY_OPTION },
                      area: { ...EMPTY_OPTION },
                      empresaDestino: { ...EMPTY_OPTION },
                    },
                  ])
                }
              />
              <DefaultButton text={t('Eliminar')} onClick={handleEliminar} disabled={gridSelection.rows.length === 0} />
              <DefaultButton text={t('Guardar')} onClick={handleOpenModel} />
            </div>
            <DialogConfirmRecoveredData
              isOpen={showRecoveredDataDialog}
              onCancel={handleCancelRecoveredDataDialog}
              onConfirm={handleConfirmRecoveredDataDialog}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostDerivationReportHoursSettlement;
